/**
 * Lead import API. List reads come from security_invoker views
 * (supabase/pg/lead/lead_import_views.sql) whose rows the lead tables' RLS
 * gates on the lead_import capability; detail reads and choices go through
 * SECURITY DEFINER RPCs (supabase/pg/lead/*.sql) that re-check it. Heavy
 * work (reading files, previews, commits, verification) is queued for the
 * GCP import worker and observed through the batch's job state. The browser
 * never parses files for the server, never drives commits and never supplies
 * hashes or rows.
 */
import { supabaseClient } from '../../utils/supabase';
import { LEGACY_XLS_MESSAGE } from '../../features/docs/drive';
import { fmtBytes } from './ui';

// The lead import RPCs postdate the generated database types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabaseClient as any;

export class LeadImportError extends Error {}

async function rpc<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await db.rpc(fn, args);
  if (error) throw new LeadImportError(error.message || `${fn} failed`);
  return data as T;
}

// PostgREST caps a response at max_rows (supabase/config.toml), so view
// reads that can outgrow it page through with .range().
const PAGE = 1000;
async function readView<T>(
  view: string,
  columns: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  narrow: (query: any) => any,
  limit = Infinity,
): Promise<T[]> {
  const rows: T[] = [];
  while (rows.length < limit) {
    const from = rows.length;
    const to = Math.min(from + PAGE, limit) - 1;
    const { data, error } = await narrow(db.from(view).select(columns)).range(from, to);
    if (error) throw new LeadImportError(error.message || `${view} failed`);
    rows.push(...(data as T[]));
    if (data.length < to - from + 1) break;
  }
  return rows;
}

async function importApi<T>(route: string, body: Record<string, unknown>): Promise<T> {
  const base = import.meta.env.VITE_LEAD_IMPORT_API_URL?.replace(/\/$/, '');
  if (!base) throw new LeadImportError('The import service is not configured. Please contact an administrator.');
  const { data: { session }, error } = await supabaseClient.auth.getSession();
  if (error || !session) throw new LeadImportError('Please sign in again to continue.');
  let response: Response;
  try {
    response = await fetch(`${base}/v1/${route}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(60_000),
    });
  } catch {
    throw new LeadImportError('The import service could not be reached. Please try again.');
  }
  const data = await response.json().catch(() => null);
  if (!response.ok || data?.error) throw new LeadImportError(data?.error || 'The import request failed. Please try again.');
  return data as T;
}

/* ── Types ────────────────────────────────────────────── */

export type BatchStatus = 'Inspecting' | 'Draft' | 'Preparing' | 'Previewed' | 'Committing' | 'Committed' | 'Failed' | 'Cancelled';

/** Worker job kinds. Phone and email checks are separate jobs (verify_phone,
 * verify_email); verify is the earlier single job that checks both. */
export type JobKind = 'inspect' | 'prepare' | 'commit' | 'verify' | 'verify_phone' | 'verify_email';
export const isVerifyKind = (kind: JobKind | undefined) => kind === 'verify' || kind === 'verify_phone' || kind === 'verify_email';

export interface StageError {
  stage: JobKind;
  class: 'transient' | 'blocked' | 'failed';
  code: string;
  message: string;
  at: string;
  retrying?: boolean;
}

export interface JobState {
  id: number;
  kind: JobKind;
  state: 'queued' | 'running' | 'done' | 'failed' | 'cancelled';
  attempts: number;
  max_attempts: number;
  progress: Record<string, number | string>;
  heartbeat_at: string | null;
  next_attempt_at: string;
  started_at: string | null;
  created_at: string;
  finished_at: string | null;
  error_class: string | null;
  /** Set on a deferred verify job: retry_scheduled | awaiting_unknown_outcomes */
  error_code: string | null;
  error_message: string | null;
  stalled: boolean;
  waiting_for_worker: boolean;
  /** Queued only: running jobs plus due jobs a worker takes first. */
  ahead: number | null;
}

export interface SourceLocation {
  provider: 'google_drive';
  file_id: string;
  name: string;
  mime_type: string;
  web_view_link?: string;
  modified_time?: string;
  version?: string;
  head_revision_id?: string;
  owner?: string;
  exported_as: string;
  capture_method?: 'drive_api' | 'manual_download';
  captured_at?: string;
}

export interface BatchListRow {
  id: number;
  file_name: string | null;
  status: BatchStatus;
  lead_source: number | null;
  source: string | null;
  sub_lead_source: string | null;
  batch_date: string | null;
  created_at: string;
  accepted_at: string | null;
  committed_at: string | null;
  observation_count: number;
  delivered_row_count: number;
  skipped: number;
  new_identity_count: number;
  known_phone_count: number;
  known_merchant_count: number;
  held_count: number;
  commit_done: number;
  commit_total: number;
  stage_error: StageError | null;
  preview_observations: number | null;
  cost: number | null;
  assigned: number;
  sheets: number;
  created_by: string | null;
  job: JobState | null;
}

export interface HeaderIssue { code: string; position?: number; detail?: string }
/** What the import does with one column. name splits a full name into first and last name. */
export type ColumnUse = 'field' | 'name' | 'phone' | 'extra' | 'ignore';
export type NameOrder = 'first_last' | 'last_first';
export interface MappingRule {
  position: number; use: ColumnUse; target?: string | null; name_order?: NameOrder | null;
  /** Suggestions only: ai, rules (the AI answer was invalid) or alias. */
  origin?: string; confidence?: number | null; reason?: string | null;
}
/** Statistics over the first rows of a column, from lead_import_column_evidence. */
export interface ColumnEvidence {
  position: number; header: string; rows_checked: number; nonblank: number; multi_word: number; comma_name: number;
  not_a_name: number; first_word_first_name: number; name_hits: Record<NameOrder, number>; phone_like: number; email_like: number;
  examples: { value: string; first_last: [string | null, string | null]; last_first: [string | null, string | null] }[];
}

export interface SheetMapping {
  version: number; version_number: number; profile: number; profile_name: string; origin: string | null;
  rules: MappingRule[]; phone_priority: number[]; headers: { position: number; header: string; occurrence: number }[];
  confirmed_at: string; signature: string;
}

export interface SheetRow {
  id: number;
  sheet_ordinal: number;
  sheet_name: string;
  selected: boolean;
  is_hidden: boolean;
  nonempty_row_count: number;
  column_count: number;
  header_cells: string[] | null;
  header_status: 'ok' | 'needs_confirmation' | 'blank_labels' | 'data_in_row1' | 'empty';
  header_issues: HeaderIssue[] | null;
  header_signature: string;
  header_confirmed: boolean;
  sample_rows: { row: number; cells: string[] }[] | null;
  mapping_version: number | null;
  mapping: SheetMapping | null;
}

export interface PreviewCounts {
  source_rows: number; header_rows: number; excluded_rows: number; delivered_rows: number;
  observations: number; collapsed_rows: number; skipped_missing_phone: number; skipped_invalid_phone: number;
  covered_rows: number; pending_mapping: number; reconciles: boolean;
}

export interface PreviewSample {
  phone_normalized: string;
  fields: Record<string, string>;
  extra_data: { unmapped?: Record<string, { v: string; o: number[] }[]>; alternatives?: Record<string, { v: string; o: number[] }[]>; extra_phones?: string[] };
  row_count: number;
  first_row: number;
  first_sheet: number;
}

export interface Preview {
  generation: number;
  prepared_at: string;
  counts: PreviewCounts;
  per_sheet: { id: number; sheet_name: string; rows: number; usable: number; skipped: number }[];
  in_file_duplicates: { phones: number; extra_rows: number };
  known: { earlier_lead: number; existing_merchant: number; both: number; new_identity: number };
  link_estimate: { single_merchant: number; competing_merchants: number };
  suppression: { dnd: number; deal_history: number; funded: number; not_assignable: number };
  extra_columns: string[];
  ignored_columns: string[];
  cell_issues: number;
  samples: PreviewSample[];
  blocking: { code: string; message: string; existing_batch?: number }[];
  can_accept: boolean;
}

/** A row skipped for lack of a usable phone, with the cells that failed. */
export interface SkippedRow {
  sheet: number; row: number; reason: 'missing_phone' | 'invalid_phone';
  phones: { header: string | null; raw: string | null }[];
  fields: Record<string, string>;
}

export type PreviewRowKind = 'leads' | 'merged' | 'skipped';
export interface PreviewRowsPage<K extends PreviewRowKind> {
  total: number; offset: number;
  rows: K extends 'skipped' ? SkippedRow[] : (PreviewSample & { origins: [number, number][] })[];
}

export interface ReadinessItem { code: string; message: string; sheet?: number }

export interface BatchDetail {
  id: number;
  file_name: string | null;
  status: BatchStatus;
  origin: string;
  lead_source: number | null;
  source: string | null;
  sub_lead_source: string | null;
  batch_date: string | null;
  batch_date_suggestion: string | null;
  content_fingerprint: string | null;
  accepted_at: string | null;
  committed_at: string | null;
  cancel_requested_at: string | null;
  created_at: string;
  preparation_generation: number;
  inspection_generation: number;
  enrichment_intent: string;
  preview: Preview | null;
  stage_error: StageError | null;
  source_location: SourceLocation | null;
  source_row_count: number; header_row_count: number; excluded_row_count: number; delivered_row_count: number;
  observation_count: number; collapsed_row_count: number; skipped_missing_phone_count: number; skipped_invalid_phone_count: number;
  covered_row_count: number; linked_count: number; held_count: number; new_identity_count: number;
  known_phone_count: number; known_merchant_count: number; commit_total: number; commit_done: number;
  commit_started_at: string | null;
  file: { id: number; sha256: string; byte_size: number; media_type: string; original_filename: string } | null;
  sheets: SheetRow[];
  readiness: ReadinessItem[];
  attempts: { id: number; existing_batch: number; attempted_batch: number | null; attempted_at: string }[];
  events: { kind: string; detail: Record<string, unknown> | null; at: string; actor: string | null }[];
  /** own: the cost of this file, entered on the import. Otherwise a share of a purchase made under Purchases. */
  allocations: { id: number; amount: number; purchase: number; invoice_reference: string | null; purchase_amount: number; own: boolean }[];
  /** fill_blanks: queued, done or failed; detail counts what the last run did. */
  effects: { kind: string; state: string; updated_at: string; detail: FillOutcome | null }[];
  open_holds: number;
  assignment_count: number;
  verification: {
    phones: number; emails: number;
    /** Checks chosen at Import; a run for every lead starts when the commit finishes. */
    on_import: VerifyKind[] | null;
    /** Each lead's stored answer, counted per answer (ok, invalid, mobile...). */
    results: { phone: Record<string, number>; email: Record<string, number> };
    credits_used: number;
    runs: {
      id: number; kinds: string[]; lead_count: number; created_at: string; force: boolean;
      counts: Record<string, number> | null;
      /** `<kind>:reused` and `<kind>:joined`: values settled without a new request. */
      reused_counts: Record<string, number> | null;
    }[];
  };
  job: JobState | null;
}

export interface MappingSuggestion {
  sheet: number;
  signature: string;
  headers: { position: number; header: string; occurrence: number }[];
  sample_rows: { row: number; cells: string[] }[];
  evidence: ColumnEvidence[];
  exact_profile: { version: number; profile: number; name: string; origin: string | null; version_number: number; rules: MappingRule[]; phone_priority: number[] } | null;
  drifted_profiles: { profile: number; name: string }[];
  suggested_rules: MappingRule[];
  suggested_phone_priority: number[];
  pinned_version: number | null;
}

export interface AiSuggestion {
  suggestion: number; provider: string; model: string;
  rules: MappingRule[]; phone_priority: number[]; rejected: { position: number; use: string; target?: string; why: string }[];
}

export interface TargetField { field_name: string; lead_column: string; ui_label: string; merchant_data_type: string; is_phone: boolean }

export interface BatchLead {
  id: number;
  phone: string | null;
  phone_normalized: string;
  business_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  state: string | null;
  city: string | null;
  industry: string | null;
  note: string | null;
  monthly_revenue: string | null;
  alternate_phone: string | null;
  /** The import this lead came from. */
  batch: number;
  file_name: string | null;
  batch_date: string | null;
  source: string | null;
  sub_source: string | null;
  origin_row: number;
  origin_sheet_name: string | null;
  merchant_link_status: 'none' | 'linked' | 'held';
  merchant: number | null;
  merchant_label: string | null;
  is_master: boolean;
  other_observations: number;
  is_new_identity: boolean;
  current_rep: number | null;
  current_rep_name: string | null;
  /** Reps a redistribution run also gave this lead to, comma separated. */
  shared_with: string | null;
  current_assignment: number | null;
  assignment_version: number;
  ever_assigned: boolean;
  eligible: boolean;
  reasons: string[];
  phone_verification_status: string | null;
  phone_verified_at: string | null;
  email_verification_status: string | null;
  email_verified_at: string | null;
}

/**
 * One filter for the leads grid and for First-N and Random-N draws. Scope
 * keys pick the imports (one import, or imports, sources and batch dates
 * across all committed imports); the others filter leads within that scope.
 */
export interface LeadFilter {
  batch?: number;
  batches?: number[];
  sources?: number[];
  /** True: every source except `sources`. */
  sources_exclude?: boolean;
  date_from?: string;
  date_to?: string;
  assignment?: 'all' | 'unassigned' | 'never_assigned' | 'assigned';
  repeat?: 'all' | 'repeats' | 'first_seen';
  eligibility?: 'all' | 'eligible' | 'not_eligible';
  rep?: number | null;
  search?: string;
  /** Leads an earlier part of the same assignment plan already takes. */
  exclude_ids?: number[];
}

export interface BatchSummary {
  total: number; assigned: number; unassigned: number; never_assigned: number; repeats: number;
  eligible: number; eligible_unassigned: number;
  not_eligible: { existing_merchant: number; deal_history: number; dnd: number; held: number };
  by_rep: { rep: number; name: string; count: number }[];
  overlap_sources: { lead_source: number | null; source: string | null; phones: number }[];
}

export type DuplicateMode = 'include_repeats' | 'exclude_any' | 'exclude_selected_sources';
export type HistoryQualifier = 'received' | 'assigned_current' | 'assigned_ever';
export type TimeBasis = 'accepted_before' | 'earlier_batch_date' | 'all_known';
export type PriorRepMode = 'none' | 'any_rep' | 'selected_reps';

export interface AssignmentControls {
  duplicate_mode?: DuplicateMode;
  history_qualifier?: HistoryQualifier;
  time_basis?: TimeBasis;
  source_ids?: number[];
  prior_rep_mode?: PriorRepMode;
  prior_rep_history?: 'current' | 'ever';
  prior_rep_ids?: number[];
}

export interface AssignmentSelection {
  /** manual: the checked ids; all: every lead the view filter matches. */
  mode: 'manual' | 'all' | 'first_n' | 'random_n';
  lead_ids?: number[];
  n?: number;
  sort?: string;
  desc?: boolean;
  view_filter?: LeadFilter;
  seed?: string;
}

/** One recipient. No count on any share means even shares of the selected leads. */
export interface AssignmentShare { rep: number; count?: number }

export interface AssignmentRequest { split: AssignmentShare[]; selection: AssignmentSelection; controls: AssignmentControls }

export interface AssignmentPreview {
  split: AssignmentShare[];
  shares: { rep: number; name: string; requested: number | null; count: number; unchanged: number; reassigned: number }[];
  selection: AssignmentSelection;
  controls: AssignmentControls;
  considered: number;
  /** False when a scope over 100,000 leads was only looked at until the draw had enough; the counts cover those leads. */
  complete: boolean;
  hard_excluded: number;
  hard_reasons: Record<string, number>;
  duplicate_excluded: number;
  duplicate_excluded_by_source: { lead_source: number; source: string; count: number }[];
  rep_excluded: number;
  rep_excluded_by_rep: { rep: number; name: string; count: number }[];
  available: number;
  requested: number;
  selected: number;
  shortfall: number;
  /** Matching leads beyond the reps' counts; they stay unassigned. */
  left_over: number;
  /** Most leads one save can give out. */
  max_leads: number;
  items: { lead: number; rep: number; expected_rep: number | null; expected_version: number }[];
  unchanged: number;
  reassignments: { lead: number; previous_rep: number; previous_rep_name: string; rep: number; rep_name: string; business_name: string | null; phone: string | null }[];
  manual_not_selectable: { lead: number; reasons: string[]; duplicate: boolean; prior_rep: boolean }[] | null;
  /** How many imports the chosen leads come from. */
  imports: number;
  evaluated_at: string;
}

export interface AssignmentListRow {
  /** Null when the assignment draws from several imports. */
  id: number; batch: number | null; file_name: string | null; batch_date: string | null; rep: number | null; rep_name: string | null;
  state: string; version: number; effective_count: number; created_at: string; created_by: string | null; note: string | null;
  /** Imports a cross-import assignment still holds leads from; null for a single import. */
  imports: { id: number; file_name: string | null; batch_date: string | null }[] | null;
  revisions: number; first_revision: { mode: string | null; requested: number | null; applied: number | null } | null;
}

export interface RepOption { id: number; name: string; is_active: boolean }
export interface SourceOption { id: number; source: string }

/* ── Reference data ───────────────────────────────────── */

export async function fetchLeadSources(): Promise<SourceOption[]> {
  const { data, error } = await supabaseClient.from('lead_source').select('id, source').order('source');
  if (error) throw error;
  return ((data ?? []) as SourceOption[]).filter((s) => s.source);
}

export async function fetchReps(): Promise<RepOption[]> {
  const { data, error } = await supabaseClient.from('rep').select('id, name, is_active').order('name');
  if (error) throw error;
  return ((data ?? []) as RepOption[]).filter((r) => r.name);
}

export const fetchTargetFields = () => rpc<TargetField[]>('lead_import_target_vocabulary');

/* ── Batches ──────────────────────────────────────────── */

/** The Imports list loads the newest batches; filters narrow it on the server. */
export const BATCH_LIMIT = 300;
export const fetchBatches = (filter: { status?: string; lead_source?: number } = {}) =>
  readView<BatchListRow>('lead_import_batch_list_v', '*', (q) => {
    let query = q.order('id', { ascending: false });
    if (filter.status) query = query.eq('status', filter.status);
    if (filter.lead_source != null) query = query.eq('lead_source', filter.lead_source);
    return query;
  }, BATCH_LIMIT);
// Batch-wide reads (status, summary, duplicate report) can take seconds on a
// large batch. 10/26: twelve status reads at once on a 192k-row batch failed
// the portal. Each read below therefore:
// - runs one at a time per batch: a refetch or invalidation while one is in
//   flight would otherwise start another, and the abandoned one still runs to
//   its timeout on the server;
// - gives a call that arrives mid-read one follow-up read that starts after
//   it (the caller may follow a change it just made); later calls share it;
// - when slow (over a second), starts the next read only three read-lengths
//   after the last finished, whatever asks: during verification the job row
//   changes every second and realtime invalidates the batch's queries every
//   2 s, bypassing the page's polling interval.
const SLOW_READ_MS = 1000;
interface PacedSlot<T> { current: Promise<T>; next?: Promise<T> }
function pacedBatchRead<T>(fn: string) {
  const slots = new Map<number, PacedSlot<T>>();
  const done = new Map<number, { at: number; ms: number }>();
  const delayFor = (batch: number): number => {
    const last = done.get(batch);
    if (!last || last.ms < SLOW_READ_MS) return 0;
    return Math.max(0, last.at + 3 * last.ms - performance.now());
  };
  const start = (batch: number): Promise<T> => {
    const delay = delayFor(batch);
    const current: Promise<T> = (delay > 0 ? new Promise<void>((resolve) => setTimeout(resolve, delay)) : Promise.resolve())
      .then(() => {
        const started = performance.now();
        return rpc<T>(fn, { p_batch: batch }).finally(() => {
          const at = performance.now();
          done.set(batch, { at, ms: at - started });
        });
      })
      .finally(() => {
        const slot = slots.get(batch);
        if (slot?.current === current && !slot.next) slots.delete(batch);
      });
    slots.set(batch, { current });
    return current;
  };
  const read = (batch: number): Promise<T> => {
    const slot = slots.get(batch);
    if (!slot) return start(batch);
    slot.next ??= slot.current.then(() => undefined, () => undefined).then(() => start(batch));
    return slot.next;
  };
  return { read, lastMs: (batch: number): number => done.get(batch)?.ms ?? 0 };
}
const batchStatus = pacedBatchRead<BatchDetail>('lead_import_batch');
export const fetchBatch = (batch: number): Promise<BatchDetail> => batchStatus.read(batch);
/** How long the last status read of this batch took, in ms (0 before the first). */
export const lastBatchReadMs = (batch: number): number => batchStatus.lastMs(batch);
/** One page of a previewed batch's leads, merged leads or skipped rows. */
export const fetchPreviewRows = <K extends PreviewRowKind>(batch: number, kind: K, offset: number, limit = 100) =>
  rpc<PreviewRowsPage<K>>('lead_import_preview_rows', { p_batch: batch, p_kind: kind, p_offset: offset, p_limit: limit });

/** The largest original the import service takes. */
export const MAX_UPLOAD_MB = 100;

export const UPLOAD_ACCEPT = '.csv,.xlsx,.tsv,.txt';

/** Why a file cannot be uploaded; drops bypass the input's accept list. */
export function uploadProblem(f: File): string | null {
  if (/\.xls$/i.test(f.name)) return LEGACY_XLS_MESSAGE;
  if (f.size > MAX_UPLOAD_MB * 1048576) return `This file is ${fmtBytes(f.size)}. Files over ${MAX_UPLOAD_MB} MB are not supported; split it into smaller files.`;
  return null;
}


/**
 * Upload a file as a new batch: the server issues a one-path upload intent,
 * the browser uploads the bytes there, then registers the intent. The GCP
 * worker reads, hashes and inspects the stored original.
 */
export async function uploadOriginal(
  file: File, onStage?: (stage: string) => void, sourceLocation?: SourceLocation,
): Promise<{ batch: number }> {
  const path = await storeOriginal(file, onStage, sourceLocation);
  onStage?.('Starting the import');
  return rpc<{ batch: number }>('lead_import_register_upload', { p_storage_path: path });
}

/**
 * Upload a corrected file for a draft. It is read as a new batch that keeps
 * the draft's source, date and cost; the draft is discarded.
 */
export async function replaceOriginal(batch: number, file: File, onStage?: (stage: string) => void): Promise<{ batch: number }> {
  const path = await storeOriginal(file, onStage);
  onStage?.('Reading the new file');
  return rpc<{ batch: number }>('lead_import_replace_upload', { p_batch: batch, p_storage_path: path });
}

async function storeOriginal(file: File, onStage?: (stage: string) => void, sourceLocation?: SourceLocation): Promise<string> {
  onStage?.('Preparing the upload');
  const ticket = await importApi<{ path: string; token: string; bucket: string }>('uploads', {
    filename: file.name, size: file.size, source_location: sourceLocation ?? null,
  });
  onStage?.('Uploading the file');
  const { error } = await supabaseClient.storage.from(ticket.bucket).uploadToSignedUrl(ticket.path, ticket.token, file, {
    contentType: file.type || 'application/octet-stream',
  });
  if (error) throw new LeadImportError(`Upload failed: ${error.message}`);
  return ticket.path;
}

export const downloadOriginalUrl = (batch: number) => importApi<{ url: string }>('download', { batch });
export const retryStage = (batch: number) => rpc<{ stage: string }>('lead_import_retry', { p_batch: batch });

export const updateBatchDetails = (batch: number, details: Record<string, unknown>) =>
  rpc('lead_import_update_details', { p_batch: batch, p_details: details });
export const selectSheets = (batch: number, sheetIds: number[]) =>
  rpc('lead_import_select_sheets', { p_batch: batch, p_sheet_ids: sheetIds });
export const confirmHeader = (batch: number, sheet: number, confirmed: boolean) =>
  rpc('lead_import_confirm_header', { p_batch: batch, p_sheet: sheet, p_confirmed: confirmed });
export const suggestMapping = (batch: number, sheet: number) =>
  rpc<MappingSuggestion>('lead_import_suggest_mapping', { p_batch: batch, p_sheet: sheet });
export const aiSuggestMapping = (batch: number, sheet: number) => importApi<AiSuggestion>('ai-suggest', { batch, sheet });
/** Names blank row-1 headers; the import service checks them and re-signs the header. */
export const nameColumns = (batch: number, sheet: number, names: Record<number, string>) =>
  importApi<{ header_status: SheetRow['header_status'] }>('name-columns', { batch, sheet, names });
export const confirmMapping = (args: {
  batch: number; sheet: number; rules: MappingRule[]; phonePriority: number[]; profile: number | null;
  profileName: string | null; origin: string; applySameLayout: boolean; suggestion: number | null;
}) => rpc<{ version: number; profile: number; sheets_pinned: number }>('lead_import_confirm_mapping', {
  p_batch: args.batch,
  p_sheet: args.sheet,
  p_rules: args.rules.map((r) => ({
    position: r.position, use: r.use,
    ...(r.use === 'field' ? { target: r.target } : {}),
    ...(r.use === 'name' ? { name_order: r.name_order ?? 'first_last' } : {}),
  })),
  p_phone_priority: args.phonePriority,
  p_profile: args.profile,
  p_profile_name: args.profileName,
  p_origin: args.origin,
  p_apply_same_layout: args.applySameLayout,
  p_suggestion: args.suggestion,
});
export const requestPrepare = (batch: number) =>
  rpc<{ queued: boolean; blocking?: ReadinessItem[] }>('lead_import_request_prepare', { p_batch: batch });
/** verify: the checks to run on every lead once the commit finishes (empty: none). */
export const acceptBatch = (batch: number, verify: VerifyKind[], recheck = false) =>
  rpc<{ accepted: boolean; existing_batch?: number; message?: string; commit_total?: number }>('lead_import_accept',
    { p_batch: batch, p_verify: verify, p_recheck: recheck });

/** The one choice every verification start offers. Off by default: leads that
 * were already in the CRM and values with a stored answer are not paid for again. */
export const RECHECK_LABEL = 'Also verify leads already in the CRM and values verified before (charges again)';
export const cancelBatch = (batch: number) => rpc('lead_import_cancel', { p_batch: batch });
export const resumeBatch = (batch: number) => rpc('lead_import_resume', { p_batch: batch });
export const discardDraft = (batch: number) => rpc('lead_import_discard_draft', { p_batch: batch });

/* ── Leads ────────────────────────────────────────────── */

/**
 * capped: count up to 10,000 matches (total_is_lower_bound when there are
 * more); exact: count every match (slow on very large views); none: no count
 * (later pages of the same view).
 */
export type LeadCount = 'capped' | 'exact' | 'none';
export interface LeadsPage { rows: BatchLead[]; total: number | null; total_is_lower_bound: boolean }
export const fetchLeads = (filter: LeadFilter, sort: string, desc: boolean, limit: number, offset: number, count: LeadCount = 'capped') =>
  rpc<LeadsPage>('lead_import_leads', {
    p_filter: filter, p_sort: sort, p_desc: desc, p_limit: limit, p_offset: offset, p_count: count,
  });
/** Every lead a view filter matches, counted exactly. */
export const fetchLeadCount = (filter: LeadFilter) =>
  fetchLeads(filter, 'origin', false, 1, 0, 'exact').then((page) => page.total ?? 0);
const batchSummary = pacedBatchRead<BatchSummary>('lead_import_batch_summary');
export const fetchBatchSummary = (batch: number): Promise<BatchSummary> => batchSummary.read(batch);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchLeadDetail = (lead: number) => rpc<any>('lead_import_lead_detail', { p_lead: lead });
export interface MerchantLead {
  lead: number; batch: number; file_name: string; batch_date: string | null; source: string | null; sub_source: string | null;
  phone: string | null; business_name: string | null; first_name: string | null; last_name: string | null;
  /** linked: the lead points at this merchant; same_phone: it only shares a phone with it. */
  relation: 'linked' | 'same_phone'; is_master: boolean; arrival_status: string | null;
  rep: number | null; rep_name: string | null; assigned_at: string | null; ever_assigned: boolean;
}
export const fetchMerchantLeads = (merchant: number) => rpc<MerchantLead[]>('lead_import_merchant_timeline', { p_merchant: merchant });

/* ── Autofill ─────────────────────────────────────────── */

/** What a fill of empty merchant fields did. error: why the last run failed. */
export interface FillOutcome { merchants?: number; filled?: number; conflicts?: number; linked_leads?: number; at?: string; error?: string }
/** A value a lead gave its merchant: filled into an empty field, or kept aside because the merchant already had a different one. */
export interface FieldFill {
  field: string; label: string; outcome: 'filled' | 'conflict'; lead_value: string;
  /** The merchant's value at the time, for a conflict. Null when the merchant is not visible to you. */
  merchant_value: string | null; at: string;
}
export interface MerchantFieldFill extends FieldFill { lead: number; source: string | null; batch_date: string | null }
export interface NeverFillField { field_name: string; ui_label: string; reason: string }
export const fetchNeverFillFields = () => rpc<NeverFillField[]>('lead_import_never_fill_fields');
export const fillBlanks = (batch: number) => rpc<FillOutcome>('lead_import_fill_blanks', { p_batch: batch });
export const fetchMerchantFieldFills = (merchant: number) => rpc<MerchantFieldFill[]>('lead_import_merchant_field_fills', { p_merchant: merchant });

/* ── Holds ────────────────────────────────────────────── */

export interface HoldRow {
  id: number; lead: number; kind: string; status: string; created_at: string; resolution: string | null; note: string | null;
  resolved_at: string | null; phone: string | null; phone_normalized: string; business_name: string | null;
  batch: number; batch_date: string | null; source: string | null;
  candidates: HoldCandidate[];
}
/** A merchant the lead could belong to. Restricted merchants carry only their label. */
export interface HoldCandidate {
  id: number | null; label: string; visible: boolean;
  status?: string | null; owner?: string | null; created_at?: string;
  /** Merchant phone fields that hold the lead's number, with the value as stored. */
  phone_matches?: { field: string; value: string }[];
  /** Leads with this phone from earlier imports that were linked to this merchant. */
  earlier_imports?: number;
  submissions?: number;
}
export const fetchHolds = (status: string | null, batch: number | null = null) =>
  rpc<HoldRow[]>('lead_link_issue_list', { p_status: status, p_batch: batch });
export const resolveHold = (issue: number, action: 'link' | 'keep_unlinked' | 'dismiss', merchant: number | null, note: string | null) =>
  rpc('lead_link_issue_resolve', { p_issue: issue, p_action: action, p_merchant: merchant, p_note: note });

/* ── Assignment ───────────────────────────────────────── */

/** The scope (one import or several) travels in selection.view_filter. */
export const previewAssignment = (request: AssignmentRequest) =>
  rpc<AssignmentPreview>('lead_assignment_preview', { p_request: request });
export interface SaveResult {
  replayed: boolean;
  /** One saved assignment per rep. */
  assignments: { assignment: number; revision: number; rep: number; applied: number; conflict: number }[];
  applied: number; conflict: number; unchanged: number; ineligible: number; departed_from_draw: number;
}
export const saveAssignment = (
  preview: AssignmentPreview, idempotencyKey: string, confirmReassign: boolean, reason: string | null, note: string | null,
) => rpc<SaveResult>(
  'lead_assignment_save', {
    p_request: { split: preview.split, selection: preview.selection, controls: preview.controls,
                 preview_summary: { selected: preview.selected, available: preview.available, shortfall: preview.shortfall,
                                    hard_excluded: preview.hard_excluded, duplicate_excluded: preview.duplicate_excluded,
                                    rep_excluded: preview.rep_excluded, evaluated_at: preview.evaluated_at } },
    p_items: preview.items,
    p_idempotency_key: idempotencyKey,
    p_confirm_reassign: confirmReassign,
    p_reason: reason,
    p_note: note,
  });
export type ExclusionKind = 'not_assignable' | 'repeat' | 'prior_rep';
/** A lead a preview left out; skipped_because names the matching sources (repeat) or reps (prior_rep). */
export type ExcludedLead = BatchLead & { skipped_because: string[] | null };
export const fetchExcluded = (request: AssignmentRequest, kind: ExclusionKind, limit: number, offset: number) =>
  rpc<{ rows: ExcludedLead[]; total: number }>('lead_assignment_excluded', { p_request: request, p_kind: kind, p_limit: limit, p_offset: offset });
export const fetchAssignments = (batch: number | null) =>
  readView<AssignmentListRow>('lead_assignment_list_v', '*', (q) =>
    (batch == null ? q : q.eq('batch', batch)).order('id', { ascending: false }));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchAssignmentDetail = (assignment: number) => rpc<any>('lead_assignment_detail', { p_assignment: assignment });
export const editAssignment = (
  assignment: number, action: 'change_rep' | 'add' | 'remove', payload: Record<string, unknown>, expectedVersion: number,
  idempotencyKey: string, reason: string | null,
) => rpc<{ revision: number; applied?: number; conflict?: number }>('lead_assignment_edit', {
  p_assignment: assignment, p_action: action, p_payload: payload, p_expected_version: expectedVersion,
  p_idempotency_key: idempotencyKey, p_reason: reason,
});
/** A rep the default plan starts with, or one listed because a rule or the Opener group names them. */
export interface OpenerRow { rep: number; name: string; in_group: boolean; rule: 'include' | 'exclude' | null; opener: boolean }
export const fetchOpeners = () => rpc<OpenerRow[]>('lead_assignment_openers');
export const setOpenerRule = (rep: number, rule: 'include' | 'exclude' | 'default') =>
  rpc<OpenerRow[]>('lead_assignment_set_opener', { p_rep: rep, p_rule: rule });
export const departingPreview = (rep: number, from: string | null, to: string | null, dateField: 'assigned_at' | 'batch_date') =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rpc<any>('lead_assignment_departing_preview', { p_rep: rep, p_from: from, p_to: to, p_date_field: dateField });

/* ── Redistribution (spec v4 A.12) ────────────────────── */

/** Leads one rep holds (optionally only some imports and batch dates), or one import's held leads. */
export type RedistributionSource =
  | { kind: 'rep'; rep: number; imports?: number[]; date_from?: string | null; date_to?: string | null }
  | { kind: 'import'; import: number };
export interface RedistributionRequest {
  source: RedistributionSource;
  receivers: { rep: number; count: number }[];
  /** Never give a lead to a rep who already had that phone, however long ago. */
  never_repeat?: boolean;
}
/** returning: leads going back to a rep who had the phone more than 30 days ago. */
export interface RedistributionSummary {
  source_total: number; eligible: number;
  excluded: { dnd: number; open_deal: number; repeat_phone: number };
  requested: number; given: number; returning: number; never_repeat: boolean;
  per_rep: { rep: number; name: string; requested: number; given: number; returning: number; holds: number; had_recently: number }[];
}
export interface RedistributionRun {
  id: number; created_at: string; by: string | null;
  source_kind: 'rep' | 'import'; source_rep: number | null; source_rep_name: string | null;
  source_import: number | null; source_file_name: string | null;
  imports: number[] | null; date_from: string | null; date_to: string | null; never_repeat: boolean;
  reason: string | null; requested: number; given: number; state: 'applied' | 'retracted';
  retracted_at: string | null; retracted_by: string | null; retract_reason: string | null;
  per_rep: { rep: number; name: string; count: number }[];
}
/** Runs over this many leads need a second confirmation. */
export const REDISTRIBUTE_CONFIRM_OVER = 1500;
export const previewRedistribution = (request: RedistributionRequest) =>
  rpc<RedistributionSummary>('lead_redistribution_preview', { p_request: request });
export const saveRedistribution = (request: RedistributionRequest, idempotencyKey: string, reason: string, confirmLarge: boolean) =>
  rpc<{ replayed: boolean; run: number; summary: RedistributionSummary }>('lead_redistribution_save', {
    p_request: request, p_idempotency_key: idempotencyKey, p_reason: reason, p_confirm_large: confirmLarge,
  });
export const retractRedistribution = (run: number, reason: string | null) =>
  rpc<{ run: number; retracted: number; already: boolean }>('lead_redistribution_retract', { p_run: run, p_reason: reason });
export const fetchRedistributionRuns = (rep: number | null, batch: number | null) =>
  rpc<RedistributionRun[]>('lead_redistribution_runs', { p_rep: rep, p_import: batch, p_limit: 50 });

/* ── Verification ─────────────────────────────────────── */

export type VerifyKind = 'phone' | 'email';

/** Per check, for a previewed batch: distinct values, how many already have a
 * stored answer, how many wait on an open check, and how many need a new paid one. */
/** Per kind, exclusive: known (leads already in the CRM, skipped by default),
 * waiting (another run is checking it), stored (answer reused), new (charged).
 * recheck: what is charged when the operator includes known and stored. */
export interface VerificationEstimateKind { values: number; known: number; waiting: number; stored: number; new: number; recheck: number }
export interface VerificationEstimate {
  phone: VerificationEstimateKind;
  email: VerificationEstimateKind;
}
export const fetchVerificationEstimate = (batch: number) =>
  rpc<VerificationEstimate>('lead_verification_estimate', { p_batch: batch });

export interface VerificationStart {
  run: number;
  replayed: boolean;
  /** New paid provider checks. */
  phone_requests?: number; email_requests?: number;
  /** Values with a stored result, applied at no cost. */
  phone_reused?: number; email_reused?: number;
  /** Values already waiting on another run's check; that answer is shared. */
  phone_joined?: number; email_joined?: number;
  skipped_blank_email?: number; skipped_malformed_email?: number;
  /** Leads already in the CRM when they arrived, left out unless forced. */
  skipped_known_leads?: number;
}
/** Each phone or email is checked once and the result stored; force charges a new check. */
export const startVerification = (batch: number, leads: number[], kinds: ('phone' | 'email')[], force: boolean, idempotencyKey: string) =>
  rpc<VerificationStart>('lead_verification_start',
    { p_batch: batch, p_leads: leads, p_kinds: kinds, p_idempotency_key: idempotencyKey, p_force: force });
export const retryFailedVerification = (run: number) => rpc<number>('lead_verification_retry_failed', { p_run: run });

/** How one lead's phone or email fared in a run. `how`: checked (this run paid
 * for it), joined (shared another run's open check), stored (an earlier answer,
 * no cost) or skipped (nothing usable to check; `skip` says why). */
export interface VerificationCheck {
  how: 'checked' | 'joined' | 'stored' | 'skipped';
  state?: 'queued' | 'sent' | 'recorded' | 'failed' | 'outcome_unknown';
  result?: string | null;
  /** The carrier for a phone; the provider's reason and flags for an email. */
  detail?: string | null;
  error?: string | null;
  at?: string | null;
  skip?: string;
}
export interface VerificationRunDetail {
  run: {
    id: number; lead_import: number; kinds: ('phone' | 'email')[]; force: boolean; lead_count: number;
    created_at: string; requested_by: string | null; credits_used: number | null;
  };
  leads: {
    id: number; business_name: string | null; first_name: string | null; last_name: string | null;
    phone: string | null; email: string | null; checks: Partial<Record<'phone' | 'email', VerificationCheck>>;
  }[];
}
export const fetchVerificationRun = (run: number) => rpc<VerificationRunDetail>('lead_verification_run_detail', { p_run: run });

/* ── Purchases, promotion, reports, exports ───────────── */

export interface PurchaseRow {
  id: number; lead_source: number; source: string | null; amount: number; currency: string; purchased_on: string | null;
  invoice_reference: string | null; note: string | null; allocated: number;
  allocations: { id: number; batch: number; amount: number; file_name: string | null; batch_date: string | null }[];
  /** The QuickBooks money-out transactions that paid this purchase; paid is their total. */
  payments: (QuickBooksPayment & { id: number })[];
  paid: number;
}
/** A QuickBooks money-out line. paid is positive; lead_source is the source the bank rules filed it under. */
export interface QuickBooksPayment {
  transaction: number; txn_date: string; paid: number; payee: string | null; memo: string | null; doc_number: string | null;
  kind: string; account_number: string | null; lead_source: number | null; source: string | null;
}
export const fetchPurchases = () =>
  readView<PurchaseRow>('lead_purchase_list_v', '*', (q) => q.order('id', { ascending: false }));
export const savePurchase = (purchase: Record<string, unknown>) => rpc<number>('lead_purchase_save', { p_purchase: purchase });
export const allocatePurchase = (purchase: number, batch: number, amount: number) =>
  rpc<number>('lead_purchase_allocate', { p_purchase: purchase, p_batch: batch, p_amount: amount });
export const removeAllocation = (allocation: number) => rpc('lead_purchase_remove_allocation', { p_allocation: allocation });
/** Unlinked QuickBooks payments, the likeliest for the purchase first. */
export const fetchPaymentCandidates = (purchase: number | null, search: string) =>
  rpc<QuickBooksPayment[]>('lead_purchase_payment_candidates', { p_purchase: purchase, p_search: search || null });
export const linkPayment = (purchase: number, transaction: number) =>
  rpc<number>('lead_purchase_link_payment', { p_purchase: purchase, p_transaction: transaction });
export const unlinkPayment = (payment: number) => rpc('lead_purchase_unlink_payment', { p_payment: payment });
/** A new purchase for the whole payment. leadSource is used when the bank rules filed no source. */
export const purchaseFromPayment = (transaction: number, leadSource: number | null) =>
  rpc<number>('lead_purchase_from_payment', { p_transaction: transaction, p_lead_source: leadSource });
/** Everything allocated to the import: its own cost plus shares of purchases. */
export const costOf = (batch: BatchDetail) => batch.allocations.reduce((n, a) => n + Number(a.amount), 0);
/** Sets the cost of one file; null or 0 removes it. */
export const setImportCost = (batch: number, amount: number | null, invoiceReference: string) =>
  rpc('lead_purchase_set_import_cost', { p_batch: batch, p_amount: amount, p_invoice_reference: invoiceReference });

export const promoteLead = (lead: number, mode: 'create' | 'attach', merchant: number | null) =>
  rpc<{ merchant: number; mode: string }>('lead_promote', { p_lead: lead, p_mode: mode, p_merchant: merchant });
export const promoteBulk = (leads: number[]) =>
  rpc<{ created: number; skipped: { lead: number; reason: string }[] }>('lead_promote_bulk', { p_leads: leads });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchSourceReport = (from: string | null, to: string | null) => rpc<any>('lead_import_report_sources', { p_from: from, p_to: to });
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchOverlapReport = (from: string | null, to: string | null) => rpc<any>('lead_import_report_overlap', { p_from: from, p_to: to });
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchSecondaryReport = (batch: number | null) => rpc<any[]>('lead_import_report_secondary', { p_batch: batch });
/** [spec v4 C1] Duplicates are leads whose phone came in an earlier import. */
export interface DuplicateReport {
  leads: number; never_seen: number; duplicates: number;
  same_source: number;
  same_source_imports: { batch: number; file_name: string | null; batch_date: string | null; leads: number }[];
  other_source: number;
  other_sources: { lead_source: number | null; source: string; leads: number }[];
  excluded: { merchant: number; dnd: number; no_phone: number };
}
const duplicateReport = pacedBatchRead<DuplicateReport>('lead_import_duplicate_report');
export const fetchDuplicateReport = (batch: number): Promise<DuplicateReport> => duplicateReport.read(batch);

/** [spec v4 C2] source: only leads with an earlier sighting from it. rep 'none': unassigned. */
export interface ExportFilter { duplicates: 'all' | 'only' | 'except'; source: number | null; rep: number | 'none' | null }
export interface ExportTable { columns: string[]; total: number; rows: (string | number | null)[][] }
/** limit null returns every row. */
export const fetchExport = (batch: number, filter: ExportFilter, limit: number | null) =>
  rpc<ExportTable>('lead_import_export', { p_batch: batch, p_filter: filter, p_limit: limit });
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchExcludedExport = (batch: number) => rpc<any[]>('lead_import_excluded_export', { p_batch: batch });
export interface CreditExportRow {
  lead: number; phone: string | null; email: string | null;
  phone_verification: string | null; email_verification: string | null; provider: string | null;
}
export const fetchCreditExport = (batch: number) =>
  readView<CreditExportRow>('lead_import_credit_export_v', 'lead, phone, email, phone_verification, email_verification, provider',
    (q) => q.eq('batch', batch).order('origin_row'));

/** Stored phone and email answers by result; imported ones came from a provider export. */
export interface VerificationAnswerCount { kind: 'phone' | 'email'; result: string; answers: number; imported: number }
export const fetchVerificationAnswerCounts = () => rpc<VerificationAnswerCount[]>('lead_verification_answer_counts');

export interface MappingProfileRow {
  id: number; name: string; lead_source: number | null; header_signature: string | null; created_at: string;
  versions: { id: number; version_number: number; confirmed_at: string; suggestion_origin: string | null;
              rules: MappingRule[]; phone_priority: number[]; headers: { position: number; header: string }[] }[];
}
export async function fetchMappingProfiles(): Promise<MappingProfileRow[]> {
  const { data, error } = await db
    .from('lead_mapping_profile')
    .select('id, name, lead_source, header_signature, created_at, versions:lead_mapping_version(id, version_number, confirmed_at, suggestion_origin, rules, phone_priority, headers)')
    .order('id', { ascending: false });
  if (error) throw new LeadImportError(error.message);
  return data as MappingProfileRow[];
}

/* ── Helpers ──────────────────────────────────────────── */

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/** CSV text of a table; values with commas, quotes or line breaks are quoted. */
export function csvText(columns: string[], rows: unknown[][]): string {
  const esc = (v: unknown) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [columns.map(esc).join(','), ...rows.map((r) => r.map(esc).join(','))].join('\n');
}

export function downloadText(filename: string, text: string, type = 'text/csv'): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** CSV download of rows already returned by an operator-only RPC. */
export function downloadCsv(filename: string, rows: Record<string, unknown>[]): void {
  if (!rows.length) return;
  const cols = Object.keys(rows[0]);
  downloadText(filename, csvText(cols, rows.map((r) => cols.map((c) => r[c]))));
}

/** A batch's stored original, fetched through a short-lived signed URL. */
export const originalQuery = (batchId: number) => ({
  queryKey: ['lead-import', 'original', batchId],
  queryFn: async () => {
    const { url } = await downloadOriginalUrl(batchId);
    const res = await fetch(url);
    if (!res.ok) throw new Error(`The original could not be downloaded (${res.status}).`);
    return res.arrayBuffer();
  },
  // Originals are immutable: keep them for the session.
  staleTime: Infinity,
  gcTime: 10 * 60_000,
});
